import {
  formatScreeningSpecialtyLabel,
  normalizeScreeningSpecialtyBucket,
} from 'src/consumer/helpers/screeningSpecialtyUtils'

export const groupEventsByContext = (events) => {
  if (!Array.isArray(events)) {
    return {}
  }
  return events.reduce((groups, event) => {
    const contextId = event.contextId || 'default' // Fallback if no contextId
    if (!groups[contextId]) {
      groups[contextId] = []
    }
    groups[contextId].push(event)
    return groups
  }, {})
}

export function prepareBookedEventDataForListing(respData) {
  const bookedEventData = respData?.['booked-event-info']?.content
  const clinicData = respData?.['clinic-brand']
  const clinicLocationData = respData?.clinic
  const nextVisitBookedEvents = respData?.['nextVisitEvents']
  const patientData = respData?.PatientInfo
  const patientContactDetailsVisibleByBookedEventId =
    respData?.patientContactDetailsVisibleByBookedEventId || {}

  return bookedEventData?.map((bookedEvent) => {
    const foundClinicBrandData = clinicData?.find(
      (clinic) => clinic?.id === bookedEvent?.clinicBrandId,
    )
    const foundClinicLocationData = clinicLocationData?.find(
      (clinicLocation) => clinicLocation?.id === bookedEvent?.clinicId,
    )
    const blockedNextVisitSlot = nextVisitBookedEvents?.find(
      (nextVisitEvent) => nextVisitEvent?.id === bookedEvent?.nextVisitReferenceBookedEventId,
    )

    const patientInfoData = patientData?.find((patient) => patient?.id === bookedEvent?.patientId)
    return {
      ...bookedEvent,
      clinicBrandData: foundClinicBrandData,
      clinicLocation: foundClinicLocationData,
      patientInfoData,
      blockedNextVisitSlot,
      patientContactDetailsVisible:
        patientContactDetailsVisibleByBookedEventId?.[bookedEvent?.id] !== false,
    }
  })
}

const SCREENING_TYPE = 'screening'

function getClinicDisplayName(clinic) {
  return clinic?.firstName || clinic?.title || clinic?.name || 'Clinic'
}

function normalizeClinicModeToMeetingSrc(mode) {
  if (mode === 'online') {
    return 'aumhum'
  }
  if (mode === 'home-visit') {
    return 'home-visit'
  }
  return 'offline'
}

function hasClinicScopedPractitionerScreening(mentorEventTypes, clinicId, targetSpecialtySlug) {
  if (!Array.isArray(mentorEventTypes) || !clinicId) {
    return false
  }
  const targetSpecialtyBucket = normalizeScreeningSpecialtyBucket(targetSpecialtySlug)
  return mentorEventTypes.some(
    (eventType) =>
      eventType?.type === SCREENING_TYPE &&
      eventType?.contextId === clinicId &&
      normalizeScreeningSpecialtyBucket(eventType?.targetSpecialtySlug || eventType?.screeningType) ===
        targetSpecialtyBucket,
  )
}

function normalizeClinicScreeningFallbackEntry(appointmentType, clinic) {
  if (!appointmentType || !clinic?.id) {
    return null
  }
  const targetSpecialtySlug = normalizeScreeningSpecialtyBucket(
    appointmentType?.targetSpecialtySlug || appointmentType?.screeningType,
  )
  return {
    id: appointmentType?.id,
    uid: `clinic-screening-${clinic.id}-${appointmentType?.id || 'fallback'}`,
    title: appointmentType?.title || 'Clinic Screening',
    type: appointmentType?.type || SCREENING_TYPE,
    durationInMins: appointmentType?.duration,
    meetingSrc: normalizeClinicModeToMeetingSrc(appointmentType?.mode),
    contextId: clinic?.id,
    clinicBrandId: appointmentType?.clinicBrandId || clinic?.clinicBrandId,
    enabled: true,
    publicEvent: false,
    free: false,
    visitEventType: appointmentType?.visitEventType,
    specializationsMentorIds: appointmentType?.specializationsMentorIds || [],
    specializationType: appointmentType?.specializationType,
    pricingDetail: appointmentType?.pricingDetail,
    targetSpecialtySlug,
    specialtyDisplayLabel: formatScreeningSpecialtyLabel(targetSpecialtySlug),
    isClinicFallbackScreening: true,
    sourceLabel: 'Clinic Screening Fallback',
    clinicDisplayName: getClinicDisplayName(clinic),
    helperText:
      'Read only. This screening comes from the clinic appointment types and is used only when no personal screening event is configured for this clinic.',
  }
}

export function buildEffectiveClinicScreeningFallbacks({
  mentorEventTypes,
  associatedClinics,
  clinicAppointmentTypesMap,
}) {
  if (!Array.isArray(associatedClinics) || !clinicAppointmentTypesMap) {
    return []
  }

  return associatedClinics.reduce((fallbacks, clinic) => {
    const clinicId = clinic?.id
    if (!clinicId) {
      return fallbacks
    }

    const effectiveScreeningAppointmentTypes = []
    const seenSpecialtyBuckets = new Set()

    ;(clinicAppointmentTypesMap?.[clinicId] || []).forEach((appointmentType) => {
      if (appointmentType?.type !== SCREENING_TYPE) {
        return
      }
      const specialtyBucket = normalizeScreeningSpecialtyBucket(
        appointmentType?.targetSpecialtySlug || appointmentType?.screeningType,
      )
      if (seenSpecialtyBuckets.has(specialtyBucket)) {
        return
      }
      seenSpecialtyBuckets.add(specialtyBucket)
      effectiveScreeningAppointmentTypes.push(appointmentType)
    })

    effectiveScreeningAppointmentTypes.forEach((appointmentType) => {
      if (
        hasClinicScopedPractitionerScreening(
          mentorEventTypes,
          clinicId,
          appointmentType?.targetSpecialtySlug || appointmentType?.screeningType,
        )
      ) {
        return
      }
      const fallbackEntry = normalizeClinicScreeningFallbackEntry(appointmentType, clinic)
      if (fallbackEntry) {
        fallbacks.push(fallbackEntry)
      }
    })
    return fallbacks
  }, [])
}

export function isClinicFallbackScreeningEntry(eventType) {
  return Boolean(eventType?.isClinicFallbackScreening)
}

export function describeClinicFallbackTargeting(fallbackEntry) {
  if (!fallbackEntry) {
    return 'Applies as the clinic default screening.'
  }

  const targetedPractitioners = Array.isArray(fallbackEntry?.specializationsMentorIds)
    ? fallbackEntry.specializationsMentorIds
    : []
  if (targetedPractitioners.length === 0) {
    return 'Applies as the clinic default screening.'
  }
  if (fallbackEntry?.specializationType === 'incl') {
    return `Applies only to ${targetedPractitioners.length} selected practitioner${targetedPractitioners.length > 1 ? 's' : ''}.`
  }
  if (fallbackEntry?.specializationType === 'excl') {
    return `Applies to the clinic except ${targetedPractitioners.length} excluded practitioner${targetedPractitioners.length > 1 ? 's' : ''}.`
  }
  return 'Practitioner targeting is configured for this clinic screening.'
}
